import * as React from "react";
import { useNavigate } from "react-router-dom";
import {
    Button,
    Dialog,
    DialogContent,
    Card,
    Badge,
} from "@jhool-io/fe-components";
import {
    IAttendeeConsentFormRecord,
    IConsentFormRecord,
    IFormDocument,
    PORTAL_SUPPORTED_FORM_TYPES,
} from "../../types/client-forms.types";
import {
    makeStringFirstLetterCapital,
    removeEnumUnderscore,
    formatDate,
} from "../../../../utils/helpers";
import {
    getStatusDisplay,
    getStatusColors,
} from "../../helpers/client-forms.helpers";
import {
    downloadFormDocumentFile,
    useGetClientConsents,
} from "../../hooks/client-forms.queries";
import useToast from "../../../../hooks/useToast";
import ConsentDocumentViewer from "../ConsentDocumentViewer/ConsentDocumentViewer";

export default function ConsentForms() {
    const [showDocumentViewer, setShowDocumentViewer] = React.useState(false);
    const [selectedDocuments, setSelectedDocuments] = React.useState<
        IFormDocument[]
    >([]);
    const [selectedConsent, setSelectedConsent] = React.useState<
        IConsentFormRecord | IAttendeeConsentFormRecord | null
    >(null);
    const [currentDocumentIndex, setCurrentDocumentIndex] = React.useState(0);
    const [isDownloading, setIsDownloading] = React.useState(false);

    const navigate = useNavigate();
    const { toast } = useToast();
    const { data, isSuccess } = useGetClientConsents({});

    const handleViewDocuments = (
        consent: IConsentFormRecord | IAttendeeConsentFormRecord
    ) => {
        if (consent.consent_documents.length > 0) {
            setSelectedDocuments(consent.consent_documents);
            setSelectedConsent(consent);
            setCurrentDocumentIndex(0);
            setShowDocumentViewer(true);
        }
    };

    const handleCloseDocumentViewer = () => {
        setShowDocumentViewer(false);
        setSelectedDocuments([]);
        setSelectedConsent(null);
        setCurrentDocumentIndex(0);
    };

    const handleDownload = async (document: IFormDocument) => {
        setIsDownloading(true);
        try {
            await downloadFormDocumentFile(document);
        } catch {
            toast({
                mode: "error",
                message: "Could not download the document.",
            });
        } finally {
            setIsDownloading(false);
        }
    };

    return (
        <>
            <Dialog
                open={showDocumentViewer}
                onOpenChange={handleCloseDocumentViewer}
            >
                <DialogContent
                    variant="drawer"
                    title="Consent document"
                    handleCloseDialog={handleCloseDocumentViewer}
                    showFooter={false}
                    wrapperClass="w-[1180px] max-w-[calc(100vw-24px)]"
                    classNames="!overflow-hidden !p-0"
                >
                    <div>
                        {selectedConsent &&
                        selectedDocuments[currentDocumentIndex] ? (
                            <ConsentDocumentViewer
                                consent={selectedConsent}
                                document={
                                    selectedDocuments[currentDocumentIndex]
                                }
                                navigation={{
                                    current: currentDocumentIndex + 1,
                                    total: selectedDocuments.length,
                                    onPrevious: () =>
                                        setCurrentDocumentIndex((previous) =>
                                            previous > 0
                                                ? previous - 1
                                                : selectedDocuments.length - 1
                                        ),
                                    onNext: () =>
                                        setCurrentDocumentIndex((previous) =>
                                            previous <
                                            selectedDocuments.length - 1
                                                ? previous + 1
                                                : 0
                                        ),
                                }}
                                onDownload={() =>
                                    handleDownload(
                                        selectedDocuments[currentDocumentIndex]
                                    )
                                }
                                isDownloading={isDownloading}
                            />
                        ) : null}
                    </div>
                </DialogContent>
            </Dialog>

            {isSuccess && data?.data && data.data.length > 0 && (
                <Card classNames="p-12">
                    <div className="text-base text-primary p-8 mb-2 uppercase">
                        Consent Forms
                    </div>

                    <div className="flex flex-col gap-12">
                        {data.data.map((consentForm) => (
                            <Card key={consentForm.consent_id} classNames="p-8">
                                <div className="flex items-start justify-between gap-4">
                                    <div>
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <p className="text-sm font-semibold text-black capitalize">
                                                {makeStringFirstLetterCapital(
                                                    removeEnumUnderscore(
                                                        consentForm
                                                            .session_details
                                                            ?.service || "—"
                                                    )
                                                )}
                                            </p>
                                            <Badge
                                                content={getStatusDisplay(
                                                    consentForm.status
                                                )}
                                                textColor={
                                                    getStatusColors(
                                                        consentForm.status
                                                    ).textColor
                                                }
                                                bgColor={
                                                    getStatusColors(
                                                        consentForm.status
                                                    ).bgColor
                                                }
                                            />
                                        </div>

                                        <p className="flex gap-2 text-xs text-primary mb-2">
                                            <span>
                                                {
                                                    consentForm.provider_details
                                                        .first_name
                                                }
                                            </span>
                                            {
                                                consentForm.provider_details
                                                    .last_name
                                            }
                                            {consentForm.provider_details
                                                .credential
                                                ? ` · ${consentForm.provider_details.credential}`
                                                : ""}
                                        </p>

                                        <div className="flex items-center gap-20 flex-wrap">
                                            {consentForm.consent_form_sent_at && (
                                                <div>
                                                    <p className="text-[10px] uppercase tracking-wide text-[rgba(11,19,43,0.4)] mb-1">
                                                        Date Sent
                                                    </p>
                                                    <p className="text-xs font-medium text-black">
                                                        {formatDate(
                                                            consentForm.consent_form_sent_at,
                                                            false,
                                                            "MM/dd/yyyy"
                                                        )}
                                                    </p>
                                                </div>
                                            )}
                                            {consentForm.client_submitted_at && (
                                                <div>
                                                    <p className="text-[10px] uppercase tracking-wide text-[rgba(11,19,43,0.4)] mb-1">
                                                        Submitted
                                                    </p>
                                                    <p className="text-xs font-medium text-black">
                                                        {formatDate(
                                                            consentForm.client_submitted_at,
                                                            false,
                                                            "MM/dd/yyyy"
                                                        )}
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="flex flex-col items-end gap-10 shrink-0">
                                        {consentForm.consent_documents?.length >
                                            0 && (
                                            <Button
                                                variant="normal"
                                                size="auto"
                                                className="flex gap-1 p-0 font-normal text-primary text-xs"
                                                onClick={() =>
                                                    handleViewDocuments(
                                                        consentForm
                                                    )
                                                }
                                            >
                                                <span>
                                                    {
                                                        consentForm
                                                            .consent_documents!
                                                            .length
                                                    }
                                                </span>
                                                document
                                                {consentForm.consent_documents!
                                                    .length !== 1
                                                    ? "s"
                                                    : ""}
                                            </Button>
                                        )}
                                        {PORTAL_SUPPORTED_FORM_TYPES.has(
                                            consentForm.consent_form_type ?? ""
                                        ) ? (
                                            <Button
                                                variant="normal"
                                                onClick={() =>
                                                    navigate(
                                                        `/forms/consent/${consentForm.session_details.booking_id}`
                                                    )
                                                }
                                                className="text-xs font-semibold text-primary underline underline-offset-2"
                                            >
                                                View form
                                            </Button>
                                        ) : (
                                            consentForm.consent_form_link && (
                                                <a
                                                    href={
                                                        consentForm.consent_form_link
                                                    }
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="text-xs font-semibold text-primary underline underline-offset-2"
                                                >
                                                    View form
                                                </a>
                                            )
                                        )}
                                    </div>
                                </div>

                                {consentForm.attendee_consents?.length > 0 && (
                                    <div className="mt-6 border-t border-strokelight pt-5">
                                        <p className="text-[10px] uppercase text-[rgba(11,19,43,0.4)] mb-3">
                                            Attendee consent
                                        </p>
                                        {consentForm.attendee_consents.map(
                                            (attendeeConsent) => (
                                                <div
                                                    key={
                                                        attendeeConsent.consent_id
                                                    }
                                                    className="flex items-center gap-3 mb-3 flex-wrap"
                                                >
                                                    {attendeeConsent
                                                        .attendee_details
                                                        .first_name ? (
                                                        <p className="text-sm font-semibold text-black capitalize">
                                                            {`${
                                                                attendeeConsent
                                                                    .attendee_details
                                                                    ?.first_name
                                                            } ${attendeeConsent.attendee_details?.last_name || ""}`}
                                                        </p>
                                                    ) : null}
                                                    {attendeeConsent
                                                        .attendee_details
                                                        .relationship && (
                                                        <p className="text-xs text-primary">
                                                            {
                                                                attendeeConsent
                                                                    .attendee_details
                                                                    .relationship
                                                            }
                                                        </p>
                                                    )}
                                                    <Badge
                                                        content={getStatusDisplay(
                                                            attendeeConsent.status
                                                        )}
                                                        textColor={
                                                            getStatusColors(
                                                                attendeeConsent.status
                                                            ).textColor
                                                        }
                                                        bgColor={
                                                            getStatusColors(
                                                                attendeeConsent.status
                                                            ).bgColor
                                                        }
                                                    />
                                                    {attendeeConsent
                                                        .consent_documents
                                                        ?.length > 0 && (
                                                        <Button
                                                            variant="normal"
                                                            size="auto"
                                                            className="flex gap-1 p-0 font-normal text-primary text-xs"
                                                            onClick={() =>
                                                                handleViewDocuments(
                                                                    attendeeConsent
                                                                )
                                                            }
                                                        >
                                                            <span>
                                                                {
                                                                    attendeeConsent
                                                                        .consent_documents
                                                                        .length
                                                                }
                                                            </span>
                                                            document
                                                            {attendeeConsent
                                                                .consent_documents
                                                                .length !== 1
                                                                ? "s"
                                                                : ""}
                                                        </Button>
                                                    )}
                                                </div>
                                            )
                                        )}
                                    </div>
                                )}
                            </Card>
                        ))}
                    </div>
                </Card>
            )}
        </>
    );
}
